import * as marietta from '../../..';
import { Dialog } from '../../../core/view';
import { Tool } from '../ToolMenu';
import { DebugLogin } from './login.debug';
import { DebugPlan, DebugSession } from './DebugSession';

type ArticleListRes = { articles: { id: string, article_name: string }[] };
type CompartmentListRes = { compartments: { id: string, name: string, lidar_data_flag: number }[] };
type PlanListRes = { plans: { id: string, name: string, number: string, webcad_data_flag: number }[] };

// Webサイトの企画管理画面（/planning）と同じAPI・クエリ
const ARTICLES_PATH = '/api/v1/articles?sort_by=1&highlow=1';
const COMPARTMENTS_PATH = '/api/v1/articles/{article}/compartments';
const PLANS_PATH = '/api/v1/articles/{article}/compartments/{compartment}/plans?type=1';
const ARTICLE_PLACEHOLDER = '{article}';
const COMPARTMENT_PLACEHOLDER = '{compartment}';
const TOOL_NAME = 'プラン選択';
const DIALOG_ID = 'debugSelectPlanDialog';
const DIALOG_TITLE = 'プラン選択（デバッグ）';
const ARTICLE_SELECT_ID = 'debugSelectArticle';
const COMPARTMENT_SELECT_ID = 'debugSelectCompartment';
const PLAN_SELECT_ID = 'debugSelectPlan';
const CURRENT_PLAN_ID = 'debugCurrentPlan';
const LOAD_BUTTON_ID = 'btnDebugLoadPlan';
const RESET_BUTTON_ID = 'btnDebugResetPlan';
const CANCEL_BUTTON_ID = 'btnDebugSelectPlanCancel';
const JSON_CONTENT_TYPE = 'application/json';
const UNAUTHORIZED_STATUS = 401;
const ALERT_TITLE = 'デバッグ';
const PLACEHOLDER_OPTION = '選択してください';
const MSG_NEED_LOGIN = '先に「LOGIN」からログインしてください。';
const MSG_SESSION_EXPIRED = 'トークンが無効です。もう一度ログインしてください。';
const MSG_FETCH_FAILED = '一覧の取得に失敗しました。';
const CURRENT_PLAN_LABEL = '読み込み中のプラン: {0}';
const NO_CURRENT_PLAN_LABEL = '読み込み中のプラン: なし（index.html の設定を使用）';
const LABEL_PLACEHOLDER = '{0}';
const LABEL_ARTICLE = '物件';
const LABEL_COMPARTMENT = '区画';
const LABEL_PLAN = 'プラン';
const LABEL_LOAD = '読み込み';
const LABEL_RESET = '読み込みプランをリセット';
const LABEL_CANCEL = 'キャンセル';
const PLAN_OPTION_FORMAT = '{name}（{number}）';
const NAME_PLACEHOLDER = '{name}';
const NUMBER_PLACEHOLDER = '{number}';
const CONTAINER_STYLE = 'width:360px;display:flex;flex-direction:column;gap:12px;padding:12px;box-sizing:border-box;';
const FIELD_STYLE = 'display:flex;flex-direction:column;gap:4px;';
// ダイアログ幅は表示時に確定するため、後から入る長い物件名で幅が広がらないよう固定する
const SELECT_STYLE = 'width:100%;box-sizing:border-box;padding:4px;';
const CLOSE_DIV = '</div>';
const EMPTY_VALUE = '';
const CHANGE_EVENT = 'change';
const CLICK_EVENT = 'click';
const KEYDOWN_EVENT = 'keydown';
const SELECT_SELECTOR = 'select';

export class DebugSelectPlan implements Tool {
  public readonly name: string = TOOL_NAME;

  public execute(): void {
    if (!DebugSession.getAuthorization()) {
      marietta.webcad.WebCADGlobal.showAlert(MSG_NEED_LOGIN, ALERT_TITLE, () => new DebugLogin().execute());
      return;
    }
    marietta.webcad.WebCADGlobal.showDialog(DebugSelectPlanDialog, true);
  }
}

class SessionExpiredError extends Error {}

class DebugSelectPlanDialog extends Dialog {
  private compartments: CompartmentListRes['compartments'] = [];

  private plans: PlanListRes['plans'] = [];

  // 選択を素早く切り替えたとき、遅れて返った古い一覧で上書きしないための世代番号
  private requestSeq: number = 0;

  public constructor(draggable: boolean, hasModal: boolean) {
    super(draggable, hasModal);
  }

  public buildContent(): void {
    const current = DebugSession.getPlan();
    const contents = `<div style="${CONTAINER_STYLE}">`
      + `<div id="${CURRENT_PLAN_ID}"></div>`
      + this.selectHtml(ARTICLE_SELECT_ID, LABEL_ARTICLE)
      + this.selectHtml(COMPARTMENT_SELECT_ID, LABEL_COMPARTMENT)
      + this.selectHtml(PLAN_SELECT_ID, LABEL_PLAN)
      + CLOSE_DIV;

    let footer = `<button id="${LOAD_BUTTON_ID}" disabled>${LABEL_LOAD}</button>`;
    if (current) footer += `<button id="${RESET_BUTTON_ID}">${LABEL_RESET}</button>`;
    footer += `<button id="${CANCEL_BUTTON_ID}">${LABEL_CANCEL}</button>`;

    this.createDialog(DIALOG_ID, DIALOG_TITLE, contents, footer);

    // プラン名はユーザー入力値なので textContent で入れる
    document.getElementById(CURRENT_PLAN_ID).textContent = current
      ? CURRENT_PLAN_LABEL.replace(LABEL_PLACEHOLDER, current.plan_name)
      : NO_CURRENT_PLAN_LABEL;

    // 選択中のキー操作が作図ショートカットに渡らないようにする
    this.viewObj.querySelectorAll(SELECT_SELECTOR).forEach((el) => {
      el.addEventListener(KEYDOWN_EVENT, (e) => e.stopImmediatePropagation());
    });
    this.getSelect(ARTICLE_SELECT_ID).addEventListener(CHANGE_EVENT, this.onChangeArticle);
    this.getSelect(COMPARTMENT_SELECT_ID).addEventListener(CHANGE_EVENT, this.onChangeCompartment);
    this.getSelect(PLAN_SELECT_ID).addEventListener(CHANGE_EVENT, this.onChangePlan);
    document.getElementById(LOAD_BUTTON_ID)?.addEventListener(CLICK_EVENT, this.loadPlan);
    document.getElementById(RESET_BUTTON_ID)?.addEventListener(CLICK_EVENT, this.resetPlan);
    document.getElementById(CANCEL_BUTTON_ID)?.addEventListener(CLICK_EVENT, this.close);

    this.loadArticles();
  }

  private async loadArticles(): Promise<void> {
    const res = await this.request<ArticleListRes>(ARTICLES_PATH, this.nextSeq());
    if (!res) return;
    this.setOptions(ARTICLE_SELECT_ID, res.articles.map((a) => ({ value: a.id, text: a.article_name })));
  }

  private readonly onChangeArticle = async (): Promise<void> => {
    const seq = this.nextSeq();
    this.setOptions(COMPARTMENT_SELECT_ID, []);
    this.setOptions(PLAN_SELECT_ID, []);
    const articleId = this.getSelect(ARTICLE_SELECT_ID).value;
    if (!articleId) return;

    const res = await this.request<CompartmentListRes>(COMPARTMENTS_PATH.replace(ARTICLE_PLACEHOLDER, articleId), seq);
    if (!res) return;
    this.compartments = res.compartments;
    this.setOptions(COMPARTMENT_SELECT_ID, this.compartments.map((c) => ({ value: c.id, text: c.name })));
  };

  private readonly onChangeCompartment = async (): Promise<void> => {
    const seq = this.nextSeq();
    this.setOptions(PLAN_SELECT_ID, []);
    const articleId = this.getSelect(ARTICLE_SELECT_ID).value;
    const compartmentId = this.getSelect(COMPARTMENT_SELECT_ID).value;
    if (!compartmentId) return;

    const path = PLANS_PATH.replace(ARTICLE_PLACEHOLDER, articleId).replace(COMPARTMENT_PLACEHOLDER, compartmentId);
    const res = await this.request<PlanListRes>(path, seq);
    if (!res) return;
    this.plans = res.plans;
    this.setOptions(PLAN_SELECT_ID, this.plans.map((p) => ({
      value: p.id,
      text: PLAN_OPTION_FORMAT.replace(NAME_PLACEHOLDER, p.name).replace(NUMBER_PLACEHOLDER, p.number),
    })));
  };

  private readonly onChangePlan = (): void => {
    const loadButton = document.getElementById(LOAD_BUTTON_ID) as HTMLButtonElement | null;
    if (loadButton) loadButton.disabled = !this.getSelect(PLAN_SELECT_ID)?.value;
  };

  /** 選択内容を保存してリロードする（起動情報は main.ts の起動時にしか読まれないため） */
  private readonly loadPlan = (): void => {
    const compartment = this.compartments.find((c) => c.id === this.getSelect(COMPARTMENT_SELECT_ID).value);
    const plan = this.plans.find((p) => p.id === this.getSelect(PLAN_SELECT_ID).value);
    if (!compartment || !plan) return;

    const debugPlan: DebugPlan = {
      article_id: this.getSelect(ARTICLE_SELECT_ID).value,
      division_id: compartment.id,
      plan_id: plan.id,
      plan_name: plan.name,
      webcad_data_flag: plan.webcad_data_flag,
      lidar_data_flag: compartment.lidar_data_flag,
    };
    DebugSession.savePlan(debugPlan);
    location.reload();
  };

  private readonly resetPlan = (): void => {
    DebugSession.clearPlan();
    location.reload();
  };

  private nextSeq(): number {
    this.requestSeq += 1;
    return this.requestSeq;
  }

  /** GETして JSON を返す。失敗時・閉じた後・古い応答のときは null（アラートは表示中の最新リクエストだけ出す） */
  private async request<T>(path: string, seq: number): Promise<T | null> {
    const url = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}${path}`;
    const isStale = (): boolean => !this.viewObj.isConnected || seq !== this.requestSeq;
    try {
      const response = await fetch(url, {
        headers: { Authorization: DebugSession.getAuthorization() ?? EMPTY_VALUE, Accept: JSON_CONTENT_TYPE },
      });
      if (response.status === UNAUTHORIZED_STATUS) throw new SessionExpiredError();
      if (!response.ok) throw new Error(response.statusText);
      const json = await response.json() as T;
      return isStale() ? null : json;
    } catch (e) {
      console.error(e);
      if (isStale()) return null;
      if (e instanceof SessionExpiredError) {
        DebugSession.clearToken();
        this.close();
        marietta.webcad.WebCADGlobal.showAlert(MSG_SESSION_EXPIRED, ALERT_TITLE, () => new DebugLogin().execute());
      } else {
        marietta.webcad.WebCADGlobal.showAlert(MSG_FETCH_FAILED, ALERT_TITLE);
      }
      return null;
    }
  }

  private selectHtml(id: string, label: string): string {
    return `<div style="${FIELD_STYLE}">`
      + `<label for="${id}">${label}</label>`
      + `<select id="${id}" style="${SELECT_STYLE}" disabled></select>`
      + CLOSE_DIV;
  }

  private getSelect(id: string): HTMLSelectElement | null {
    return this.viewObj.querySelector(`#${id}`);
  }

  /** 選択肢を差し替える。名前はユーザー入力値なので Option 経由で入れる */
  private setOptions(id: string, options: { value: string, text: string }[]): void {
    const select = this.getSelect(id);
    if (!select) return;
    select.replaceChildren(new Option(PLACEHOLDER_OPTION, EMPTY_VALUE), ...options.map((o) => new Option(o.text, o.value)));
    select.disabled = options.length === 0;
    this.onChangePlan();
  }
}
